import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import HeroSection from "@/components/modules/HeroSection";
import ListingsModule from "@/components/modules/ListingsModule";
import DibsWatermark from "@/components/modules/DibsWatermark";
import { Loader2 } from "lucide-react";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";

interface BusinessData {
  business_id: string;
  business_name: string | null;
  description: string | null;
  avatar_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  address: string | null;
  phone_number: string | null;
  business_email: string | null;
  resolved_outlet_id?: string | null;
  subscription_tier?: string | null;
}

const AdminDashboard = () => {
  const { id, slug } = useParams<{ id?: string, slug?: string }>();
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBusiness = async () => {
      const identifier = id || slug;
      if (!identifier) {
        setError("Invalid business URL");
        setLoading(false);
        return;
      }

      try {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

        if (isUuid) {
          // UUID lookup. Fetch from businesses table directly
          const { data, error: fetchError } = await supabase
            .from("businesses")
            .select("business_id, business_name, description, avatar_url, instagram_url, facebook_url, twitter_url, phone_number, business_email, subscription_tier, slug")
            .eq("business_id", identifier)
            .maybeSingle();

          if (fetchError) throw fetchError;
          if (!data) {
            setError("Business Not Found");
          } else {
            setBusiness({
              business_id: data.business_id,
              business_name: data.business_name,
              description: data.description,
              avatar_url: data.avatar_url,
              instagram_url: data.instagram_url,
              facebook_url: data.facebook_url,
              twitter_url: data.twitter_url,
              address: null,
              phone_number: data.phone_number,
              business_email: data.business_email,
              subscription_tier: data.subscription_tier,
            });
          }
        } else {
          // Slug lookup. Try outlets first, then businesses as fallback
          const { data: outletData, error: outletError } = await supabase
            .from("outlets")
            .select("*, businesses(business_id, subscription_tier)")
            .eq("slug", identifier)
            .maybeSingle();

          if (outletError) throw outletError;

          if (outletData) {
            if (outletData.is_active === false) {
              setError("Business Not Found");
            } else {
              // Map outlet fields to the BusinessData shape
              const bizJoin = outletData.businesses as any;
              setBusiness({
                business_id: bizJoin?.business_id || outletData.business_id,
                resolved_outlet_id: outletData.outlet_id,
                business_name: outletData.outlet_name,
                description: outletData.description,
                avatar_url: outletData.avatar_url,
                instagram_url: outletData.instagram_url,
                facebook_url: outletData.facebook_url,
                twitter_url: outletData.twitter_url,
                address: outletData.address,
                phone_number: outletData.phone_number,
                business_email: outletData.business_email,
                subscription_tier: bizJoin?.subscription_tier || null,
              });
            }
          } else {
            // Fallback: check businesses table for legacy slugs
            const { data: bizData, error: bizError } = await supabase
              .from("businesses")
              .select("business_id, business_name, description, avatar_url, instagram_url, facebook_url, twitter_url, phone_number, business_email, subscription_tier, slug")
              .eq("slug", identifier)
              .maybeSingle();

            if (bizError) throw bizError;
            if (!bizData) {
              setError("Business Not Found");
            } else {
              setBusiness({
                business_id: bizData.business_id,
                business_name: bizData.business_name,
                description: bizData.description,
                avatar_url: bizData.avatar_url,
                instagram_url: bizData.instagram_url,
                facebook_url: bizData.facebook_url,
                twitter_url: bizData.twitter_url,
                address: null,
                phone_number: bizData.phone_number,
                business_email: bizData.business_email,
                subscription_tier: bizData.subscription_tier,
              });
            }
          }
        }
      } catch (err) {
        console.error("Error fetching business:", err);
        setError("Failed to load business details");
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id, slug]);

  // Dynamic favicon & title for Growth+ businesses
  useDynamicFavicon(business?.avatar_url, business?.business_name, business?.subscription_tier);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mb-4">
          <span className="text-2xl font-bold shrink-0">!</span>
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2">{error || "Business Not Found"}</h1>
        <p className="text-muted-foreground">The business you are looking for does not exist or is currently unavailable.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Mobile: Stacked layout / Desktop: Side-by-side layout */}
        <div className="flex flex-col md:flex-row md:gap-12">
          {/* Hero - Sticky sidebar on desktop */}
          <div className="md:w-72 md:shrink-0 mb-8 md:mb-0">
            <div className="md:sticky md:top-8">
              <HeroSection business={business} />
            </div>
          </div>

          {/* Modules - Scrollable content */}
          <div className="flex-1 space-y-4">
            <ListingsModule businessId={business.business_id} initialOutletId={business.resolved_outlet_id} />
          </div>
        </div>
      </div>

      {/* Mobile floating watermark */}
      <DibsWatermark />
    </div>
  );
};

export default AdminDashboard;
